/**
 * Custom hook for contract file search using Elasticsearch
 * 
 * Supports filtering, pagination, and full-text search
 */
import { useQuery } from '@tanstack/react-query'
import {
  searchContractFiles,
  type ContractFileSearchFilters,
  type ContractFileSearchResponse,
} from '../api/contractFileSearchApi'

/**
 * Hook for searching contract files with Elasticsearch
 * 
 * @param filters - Search filters
 * @param options - React Query options
 * @returns Query result with contract file data
 */
export function useContractFileSearch(
  filters: ContractFileSearchFilters | null,
  options: {
    enabled?: boolean
    staleTime?: number
  } = {}
) {
  const safeFilters: ContractFileSearchFilters = filters ?? { q: '' }
  const { enabled = true, staleTime = 30000 } = options

  const query = useQuery<ContractFileSearchResponse, Error>({
    queryKey: ['contract-file-search', safeFilters],
    queryFn: () => searchContractFiles(safeFilters),
    enabled: enabled && !!safeFilters.q && safeFilters.q.trim().length > 0,
    staleTime,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    files: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    maxScore: query.data?.max_score,
    hasMore: query.data?.page?.has_more ?? false,
    currentPage: Math.floor((safeFilters.from ?? 0) / (safeFilters.size ?? 20)),
  }
}

// Re-export types
export type { ContractFileSearchFilters, ContractFileSearchResponse }
